import React, { useEffect, useState } from "react";

import * as Sentry from "@sentry/react";
import { useSearchParams } from "react-router-dom";

import CheckoutStepper from "@app/analytics/account/Billing/components/CheckoutStepper";
import useCheckout from "@app/analytics/account/Billing/hooks/useCheckout";
import type {
  BillingSubscriptionCheckoutStep,
  NewSubscription,
  PaymentFormElementOptions,
} from "@app/analytics/account/Billing/types";
import { isFreePlan } from "@app/analytics/account/Billing/utils";
import * as BillingUtils from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import * as Events from "@app/analytics/tracking/events";
import { showToast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";
import type {
  BillingCadence,
  BillingPlan,
  CheckoutBillingInfo,
  InitiateCheckoutResponse,
  PreviewInvoice,
} from "@app/common/types";
import { isResponseErrorWithStatus } from "@app/common/utils";
import Config from "@app/config";
import usePageTitle from "@app/utils/hooks/usePageTitle";
import { titleize } from "@app/utils/utils";

import Alert from "@rui/Alert";
import Link from "@rui/Link";
import LoadSpinner from "@rui/LoadSpinner";
import Stack from "@rui/Stack";

import BillingSubscriptionCheckoutBreakdown from "./BillingSubscriptionCheckoutBreakdown";
import BillingSubscriptionCheckoutDetails from "./BillingSubscriptionCheckoutDetails";
import BillingSubscriptionCheckoutPurchase from "./BillingSubscriptionCheckoutPurchase";
import { LeftPanel, RightPanel } from "../styles";

const BillingSubscriptionCheckout = () => {
  const [step, setStep] = useState<BillingSubscriptionCheckoutStep>("details");
  usePageTitle(`Billing - Subscription Checkout - ${titleize(step)}`);

  const [queryParams] = useSearchParams();
  const { subscription, licenses, subscriptionIsError } = useBillingContext();
  const {
    checkoutBillingInfo,
    initiateSubscriptionCheckout,
    isCheckoutBillingInfoLoading,
  } = useCheckout();

  const initialPlan =
    (queryParams.get("p") as BillingPlan) ?? subscription.plan;
  const initialCadence =
    (queryParams.get("c") as BillingCadence) ??
    subscription.billingCadence ??
    "annual";

  const subscriptionPlan = isFreePlan(initialPlan) ? "enterprise" : initialPlan;
  const minimumLicenseCount =
    Config.billing.licenseMinimum[subscriptionPlan] ?? 1;

  const [newSubscription, setNewSubscription] = useState<NewSubscription>({
    plan: subscriptionPlan,
    cadence: initialCadence,
    quantity: Math.max(licenses.numActive, minimumLicenseCount),
  });

  // TODO: would be nice to cancel the intent if the user leaves the checkout flow
  useEffect(() => {
    if (licenses.numActive > newSubscription.quantity) {
      // sync the subscription quantity if the subscription loaded after the state value
      // was initialized
      setNewSubscription((newSub) => ({
        ...newSub,
        quantity: Math.max(minimumLicenseCount, licenses.numActive),
      }));
    }
  }, [licenses.numActive, minimumLicenseCount]);

  const [paymentInfoElementOptions, setPaymentInfoElementOptions] =
    useState<PaymentFormElementOptions | null>(null);
  const [previewInvoice, setPreviewInvoice] = useState<PreviewInvoice | null>(
    null
  );

  const handleUpdateCadence = (newCadence: BillingCadence) => {
    setNewSubscription((newSub) => ({ ...newSub, cadence: newCadence }));
  };

  const handleUpdateLicenses = (
    update: { delta: -1 | 1 } | { count: number }
  ) => {
    setNewSubscription((newSub) => ({
      ...newSub,
      quantity:
        "count" in update ? update.count : newSub.quantity + update.delta,
    }));
  };

  const performInitiateSubscriptionCheckout = (
    billingInfo: CheckoutBillingInfo,
    options?: {
      onSuccess?: (response: InitiateCheckoutResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    Sentry.addBreadcrumb({ message: "handleSubmitBillingInfo", level: "log" });
    initiateSubscriptionCheckout.mutate(
      {
        billingInfo,
        subscription: newSubscription,
      },
      {
        onSuccess: (response) => {
          setPreviewInvoice(response.invoice);
          setPaymentInfoElementOptions({
            clientSecret: response.clientSecret,
            type: response.clientSecretType,
          });
          options?.onSuccess?.(response);
        },
        onError: (error) => {
          Events.initiateCheckoutError();
          options?.onError?.(error);

          if (isResponseErrorWithStatus(error, 422)) {
            showToast({
              sticky: true,
              severity: "error",
              message:
                "Please reach out to support@read.ai for purchases over $1M USD",
            });
            return;
          }

          Sentry.captureException(error);
          if (billingInfo?.address) {
            showToast({
              severity: "error",
              message:
                "Oops! Something went wrong and your address could not be saved. Please confirm your address or reach out to support@read.ai if the problem persists.",
            });
          } else {
            showToast({
              severity: "error",
              message:
                "Oops! Something went wrong and checkout cannot be completed at this time.",
              sticky: true,
            });
          }
        },
      }
    );
  };

  const handleContinueToPurchase = () => {
    setStep("purchase");
    const existingAddress =
      BillingUtils.getFormattedAddress(checkoutBillingInfo);
    if (existingAddress) {
      performInitiateSubscriptionCheckout(checkoutBillingInfo!);
    }
  };

  const handleChangeBillingInfo = (
    checkoutBillingInfo: CheckoutBillingInfo,
    options?: {
      onSuccess?: (response: InitiateCheckoutResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    performInitiateSubscriptionCheckout(checkoutBillingInfo, options);
  };

  return (
    <Stack direction="row" sx={{ height: "100%" }}>
      <LeftPanel variant="center">
        <BillingSubscriptionCheckoutBreakdown
          invoice={previewInvoice}
          isLoading={initiateSubscriptionCheckout.isPending}
          subscription={newSubscription}
        />
      </LeftPanel>
      <RightPanel variant="center">
        {subscriptionIsError ? (
          <Alert embedded severity="error">
            Sorry! The checkout process cannot be completed at this time. Please
            refresh this page or contact{" "}
            <Link href={LINKS.EMAIL.SUPPORT}>support@read.ai</Link> if the
            problem persists.
          </Alert>
        ) : (
          <>
            <CheckoutStepper currentPage={step} type="subscription" />
            {step === "details" ? (
              <BillingSubscriptionCheckoutDetails
                isLoading={
                  initiateSubscriptionCheckout.isPending ||
                  isCheckoutBillingInfoLoading
                }
                onChangeCadence={handleUpdateCadence}
                onChangeLicenses={handleUpdateLicenses}
                onContinue={handleContinueToPurchase}
                subscription={newSubscription}
              />
            ) : step === "purchase" ? (
              <BillingSubscriptionCheckoutPurchase
                isLoading={initiateSubscriptionCheckout.isPending}
                onChangeBillingInfo={handleChangeBillingInfo}
                paymentInfoElementOptions={paymentInfoElementOptions}
                subscription={newSubscription}
              />
            ) : (
              <LoadSpinner />
            )}
          </>
        )}
      </RightPanel>
    </Stack>
  );
};

export default BillingSubscriptionCheckout;
